import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play } from 'lucide-react';
import { useTenant } from '../../../context/TenantContext';
import { focalPos } from '../../course-builder/slides/FocalPointPicker';
import { warmInlineEm } from './warmInlineEm';

const DEFAULTS = {
  headline: "Grow into the person you've *been becoming.*",
  subtitle:
    'A small school for adults who want to learn alongside other people who care. Honest courses, generous teachers, and a community that actually shows up.',
  ctaText: 'Find your programme',
  ctaHref: '/courses',
};

export const WarmHero: React.FC = () => {
  const { tenant } = useTenant();
  const hero = tenant?.branding.marketing?.hero ?? {};
  const headline = hero.headline || DEFAULTS.headline;
  const subtitle = hero.subtitle || DEFAULTS.subtitle;
  const ctaText = hero.ctaText || DEFAULTS.ctaText;
  const ctaHref = hero.ctaHref || DEFAULTS.ctaHref;
  const eyebrow = hero.eyebrow?.trim() || 'Spring cohort opens 4 May';
  const imageUrl = hero.imageUrl;

  return (
    <section id="hero" className="warm-hero relative overflow-hidden pt-12 pb-20 md:pt-14 md:pb-24">
      {/* Soft organic blob backdrops */}
      <span className="absolute -top-32 -right-32 w-[540px] h-[540px] rounded-full bg-[var(--warm-accent-soft)] blur-3xl opacity-55 pointer-events-none" />
      <span className="absolute -bottom-32 left-[10%] w-[380px] h-[380px] rounded-full bg-[var(--warm-sage-soft)] blur-3xl opacity-70 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-[1.1fr_1fr] gap-10 md:gap-16 items-center">
        {/* Text */}
        <div>
          <div className="inline-flex items-center gap-3 bg-[var(--warm-paper-deep)] px-4 py-2 rounded-full text-sm font-bold text-[var(--warm-accent-deep)] mb-7">
            <span className="warm-pulse w-2 h-2 rounded-full bg-[var(--warm-accent)]" />
            {eyebrow}
          </div>

          <h1 className="font-warm-serif text-[44px] sm:text-5xl md:text-6xl lg:text-[88px] leading-[1.02] tracking-[-0.02em] mb-8 text-balance text-[var(--warm-ink)]">
            {warmInlineEm(headline)}
          </h1>

          <p className="text-lg md:text-xl text-[var(--warm-ink-soft)] mb-9 leading-[1.55] max-w-[50ch]">
            {subtitle}
          </p>

          <div className="flex items-center gap-5 flex-wrap">
            <Link
              to={ctaHref}
              className="warm-btn-primary group inline-flex items-center gap-3 px-7 py-4 text-base font-bold rounded-full"
            >
              {ctaText}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-3 bg-white text-[var(--warm-ink)] px-6 py-4 rounded-full font-semibold border border-[var(--warm-rule)] hover:bg-[var(--warm-paper-deep)] transition-colors"
            >
              <span className="w-7 h-7 rounded-full bg-[var(--warm-accent)] text-white inline-flex items-center justify-center">
                <Play className="h-3 w-3 ml-0.5" fill="currentColor" />
              </span>
              Watch our story
            </Link>
          </div>

          {/* Handwritten note with curved arrow */}
          <div className="warm-handnote mt-7 inline-flex items-center gap-3 -rotate-2">
            <svg width="32" height="28" viewBox="0 0 28 24" fill="none" aria-hidden="true">
              <path
                d="M2 12 Q 14 4, 24 14 M 24 14 L 18 10 M 24 14 L 22 19"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round"
              />
            </svg>
            New here? Start with a free intro session.
          </div>
        </div>

        {/* Photo collage — three circles */}
        <div className="relative aspect-square max-w-[440px] mx-auto md:mx-0 md:ml-auto w-full">
          {/* Sticker badge — handwritten */}
          <div className="warm-sticker-hand absolute top-[8%] -left-[6%] -rotate-[5deg] z-10">
            100% real humans ✿
          </div>
          {/* Sticker badge — count */}
          <div className="warm-sticker absolute bottom-[28%] -right-[8%] rotate-[4deg] z-10 inline-flex items-center gap-2.5">
            <span className="w-7 h-7 rounded-xl bg-[var(--warm-sage-soft)] inline-flex items-center justify-center text-sm">★</span>
            <span className="text-sm font-bold text-[var(--warm-ink)]">Cohort of 24</span>
          </div>

          {/* Center photo — large */}
          <div
            className="absolute top-[6%] left-[18%] w-[62%] h-[62%] rounded-full overflow-hidden bg-[var(--warm-paper-deep)] shadow-[0_20px_50px_rgba(61,40,23,0.18)]"
            style={{
              backgroundImage: imageUrl
                ? `url(${imageUrl})`
                : 'linear-gradient(135deg, var(--warm-accent), var(--warm-accent-deep))',
              backgroundSize: 'cover',
              backgroundPosition: imageUrl ? focalPos(hero.imagePosition) : 'center',
            }}
          />
          {/* Bottom-left photo */}
          <div
            className="absolute bottom-[4%] left-0 w-[36%] h-[36%] rounded-full overflow-hidden border-[8px] border-[var(--warm-paper)] shadow-[0_12px_30px_rgba(61,40,23,0.15)]"
            style={{ background: 'linear-gradient(135deg, var(--warm-sage-soft), #5b8a72)' }}
          />
          {/* Top-right photo */}
          <div
            className="absolute top-[8%] right-0 w-[32%] h-[32%] rounded-full overflow-hidden border-[8px] border-[var(--warm-paper)] shadow-[0_12px_30px_rgba(61,40,23,0.15)]"
            style={{ background: 'linear-gradient(135deg, var(--warm-accent-soft), var(--warm-accent))' }}
          />
        </div>
      </div>
    </section>
  );
};
